import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import MyYuukke from "../../../api/MyYuukke";

// ─── Design tokens ────────────────────────────────────────────────────────────
const P   = "#a00030";
const PL  = "rgba(160,0,48,0.07)";
const PM  = "rgba(160,0,48,0.2)";
const PD  = "rgb(110,0,32)";
const TOTAL_Q = 10;

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `

  :root {

    --fs-hero:  clamp(2rem,   7vw,  3.6rem);
    --fs-h2:    clamp(1.7rem, 5vw,  2.8rem);
    --fs-body:  clamp(1rem,   2.5vw, 1.1rem);
    --fs-sm:    clamp(0.85rem,2vw,  0.95rem);
    --fs-label: clamp(0.72rem,1.8vw, 0.8rem);
    --fs-chip:  clamp(0.88rem,2.2vw, 0.98rem);
    --sp-xs: clamp(6px,  1.5vw, 10px);
    --sp-sm: clamp(12px, 3vw,   18px);
    --sp-md: clamp(20px, 5vw,   32px);
    --sp-lg: clamp(32px, 7vw,   56px);
    --radius:    14px;
    --radius-sm: 10px;
    --radius-pill: 999px;
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-text-size-adjust: 100%; scroll-behavior: smooth; }
  .pf-outer { font-family: var(--ff-body); background: #f9f7f4; min-height: 100vh; color: #1a1a18; }
  .pf-topbar {
    position: sticky; top: 0; z-index: 200;
    background: rgba(255,255,255,0.96); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(0,0,0,0.07); padding: 0 var(--sp-md);
    display: flex; align-items: center; justify-content: space-between;
    height: clamp(52px, 10vw, 66px);
  }
  .pf-logo { font-family: var(--ff-display); font-size: clamp(1.3rem, 4vw, 1.8rem); font-weight: 700; color: rgb(110,0,32); letter-spacing: -0.5px; }
  .pf-progress-wrap { display: flex; align-items: center; gap: 10px; }
  .pf-progress-track { width: clamp(90px, 22vw, 180px); height: 5px; background: #ece9e6; border-radius: 99px; overflow: hidden; }
  .pf-progress-fill { height: 100%; background: linear-gradient(90deg, rgb(160,0,48), rgb(210,50,90)); border-radius: 99px; transition: width 0.5s ease; }
  .pf-pct { font-size: var(--fs-sm); color: #bbb; min-width: 28px; text-align: right; }
  .pf-content { max-width: 700px; margin: 0 auto; padding: var(--sp-md) var(--sp-md) 120px; }
  .pf-dotbar { display: flex; gap: 5px; align-items: center; margin-bottom: var(--sp-lg); overflow: hidden; }
  .pf-dot-count { font-size: var(--fs-sm); color: #bbb; white-space: nowrap; margin-left: 8px; }
  .pf-phase-tag {
    display: inline-block; font-size: var(--fs-label); font-weight: 700; color: rgb(160,0,48);
    background: rgba(160,0,48,0.07); padding: 4px 14px; border-radius: var(--radius-pill);
    letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 12px;
  }
  .pf-q-num { font-size: var(--fs-sm); color: #ccc; margin-left: 8px; }
  .pf-q-title { font-family: var(--ff-display); font-size: var(--fs-h2); font-weight: 400; color: #1a1a18; line-height: 1.18; letter-spacing: -0.5px; margin-bottom: 10px; }
  .pf-q-hint { font-size: var(--fs-body); color: #888; line-height: 1.7; margin: 0; }
  .pf-label { display: block; font-size: var(--fs-label); font-weight: 700; color: #555; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 8px; }
  .pf-label-sub { font-size: var(--fs-sm); color: #bbb; text-transform: none; font-weight: 400; letter-spacing: 0; margin-left: 6px; }
  .pf-input, .pf-textarea, .pf-select {
    width: 100%; padding: clamp(12px,3vw,16px) clamp(14px,3.5vw,20px);
    font-family: var(--ff-body); font-size: var(--fs-body); color: #1a1a18; background: #fff;
    border: 1.5px solid rgba(0,0,0,0.12); border-radius: var(--radius); outline: none;
    appearance: none; -webkit-appearance: none; transition: border-color 0.15s, box-shadow 0.15s;
  }
  .pf-textarea { resize: vertical; line-height: 1.7; }
  .pf-input:focus, .pf-textarea:focus, .pf-select:focus { border-color: rgb(160,0,48); box-shadow: 0 0 0 3px rgba(160,0,48,0.07); }
  .pf-select-wrap { position: relative; }
  .pf-arrow { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #aaa; font-size: 11px; }
  .pf-chips { display: flex; flex-wrap: wrap; gap: clamp(6px,1.5vw,10px); margin-bottom: 20px; }
  .pf-chip { padding: clamp(10px,2.5vw,14px) clamp(16px,3.5vw,22px); border-radius: var(--radius-pill); font-family: var(--ff-body); font-size: var(--fs-chip); cursor: pointer; line-height: 1.3; transition: all 0.15s; -webkit-tap-highlight-color: transparent; }
  .pf-chip:active { transform: scale(0.97); }
  .pf-star { font-size: clamp(2.4rem,8vw,3.2rem); cursor: pointer; line-height: 1; user-select: none; -webkit-user-select: none; display: inline-block; transition: color 0.15s, transform 0.1s; -webkit-tap-highlight-color: transparent; }
  .pf-star:active { transform: scale(1.22) !important; }
  .pf-nav { display: flex; gap: 10px; margin-top: clamp(24px,6vw,44px); }
  .pf-back { padding: clamp(13px,3vw,17px) clamp(18px,4vw,28px); background: #fff; color: #777; border: 1.5px solid rgba(0,0,0,0.12); border-radius: var(--radius); font-family: var(--ff-body); font-size: var(--fs-body); cursor: pointer; transition: border-color 0.15s; -webkit-tap-highlight-color: transparent; min-height: 52px; }
  .pf-next { flex: 1; min-height: 52px; padding: clamp(13px,3.5vw,18px) clamp(20px,5vw,32px); background: rgb(160,0,48); color: #fff; border: none; border-radius: var(--radius); font-family: var(--ff-body); font-size: clamp(1rem,2.5vw,1.1rem); font-weight: 600; cursor: pointer; box-shadow: 0 4px 20px rgba(160,0,48,0.22); transition: background 0.15s, transform 0.1s, box-shadow 0.15s; -webkit-tap-highlight-color: transparent; }
  .pf-next:active { transform: scale(0.98); box-shadow: 0 2px 8px rgba(160,0,48,0.15); }
  .pf-next:disabled { background: #e0e0e0; color: #aaa; box-shadow: none; cursor: not-allowed; }
  .pf-info { background: rgba(160,0,48,0.07); border: 1px solid rgba(160,0,48,0.2); border-radius: var(--radius); padding: clamp(12px,3vw,16px) clamp(14px,3.5vw,18px); font-size: var(--fs-body); color: rgb(110,0,32); margin-bottom: 20px; line-height: 1.65; display: flex; gap: 10px; }
  .pf-skip { text-align: center; margin-top: 14px; font-size: var(--fs-sm); color: #bbb; cursor: pointer; -webkit-tap-highlight-color: transparent; }
  .pf-review-card { background: #fff; border: 1px solid rgba(0,0,0,0.07); border-radius: var(--radius); padding: clamp(12px,3vw,16px) clamp(14px,3.5vw,18px); display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 8px; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
  .pf-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(8px,2vw,12px); }
  @media (max-width: 420px) { .pf-grid-2 { grid-template-columns: 1fr; } }
  .pf-ring-card { background: #fff; border: 1px solid rgba(0,0,0,0.07); border-radius: 16px; padding: clamp(20px,5vw,32px) var(--sp-md); margin-bottom: 20px; text-align: center; box-shadow: 0 2px 16px rgba(0,0,0,0.06); }
  .pf-welcome-icon { width: clamp(64px,15vw,88px); height: clamp(64px,15vw,88px); border-radius: 50%; background: rgba(160,0,48,0.07); border: 1.5px solid rgba(160,0,48,0.2); margin: 0 auto var(--sp-md); display: flex; align-items: center; justify-content: center; font-size: clamp(1.8rem,6vw,2.6rem); }
  /* ── Video ── */
  .pf-video-viewport { position: relative; background: #1a1a18; border-radius: var(--radius); overflow: hidden; aspect-ratio: 16/9; margin-bottom: 16px; display: flex; align-items: center; justify-content: center; }
  .pf-video-viewport video { width: 100%; height: 100%; object-fit: cover; display: block; }
  .pf-video-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: rgba(255,255,255,0.4); font-size: var(--fs-sm); width: 100%; height: 100%; position: absolute; top: 0; left: 0; pointer-events: none; }
  .pf-video-placeholder svg { opacity: 0.3; }
  .pf-rec-badge { position: absolute; top: 12px; left: 12px; background: #e53935; color: #fff; font-size: 11px; font-weight: 700; border-radius: 4px; padding: 3px 8px; letter-spacing: 1.5px; display: flex; align-items: center; gap: 5px; z-index: 10; }
  .pf-rec-dot { width: 7px; height: 7px; border-radius: 50%; background: #fff; animation: pfBlink 1s infinite; }
  @keyframes pfBlink { 0%,100%{opacity:1} 50%{opacity:0.2} }
  .pf-timer-badge { position: absolute; top: 12px; right: 12px; background: rgba(0,0,0,0.55); color: #fff; font-size: 13px; font-weight: 600; border-radius: 6px; padding: 3px 10px; font-variant-numeric: tabular-nums; z-index: 10; }
  .pf-teleprompter-overlay { position: absolute; bottom: 0; left: 0; right: 0; background: linear-gradient(transparent, rgba(0,0,0,0.85)); padding: 32px 18px 16px; font-size: var(--fs-sm); color: rgba(255,255,255,0.92); line-height: 1.7; pointer-events: none; letter-spacing: 0.01em; z-index: 10; }
  .pf-video-controls { display: flex; gap: 10px; margin-bottom: 16px; }
  .pf-ctrl-btn { flex: 1; min-height: 46px; display: flex; align-items: center; justify-content: center; gap: 7px; border-radius: var(--radius-sm); font-family: var(--ff-body); font-size: var(--fs-sm); font-weight: 600; cursor: pointer; border: 1.5px solid rgba(0,0,0,0.12); transition: all 0.15s; -webkit-tap-highlight-color: transparent; }
  .pf-ctrl-btn.record { background: rgb(160,0,48); color: #fff; border-color: rgb(160,0,48); box-shadow: 0 3px 14px rgba(160,0,48,0.25); }
  .pf-ctrl-btn.pause  { background: #fff4e0; color: #7a4800; border-color: #f0c060; }
  .pf-ctrl-btn.stop   { background: #fff; color: #555; }
  .pf-ctrl-btn.retake { background: #fff; color: rgb(160,0,48); border-color: rgba(160,0,48,0.3); }
  .pf-ctrl-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .pf-speed-row { display: flex; gap: 8px; margin-bottom: 16px; }
  .pf-speed-btn { flex: 1; padding: 9px 0; border-radius: var(--radius-sm); font-family: var(--ff-body); font-size: var(--fs-sm); font-weight: 600; cursor: pointer; border: 1.5px solid rgba(0,0,0,0.1); background: #fff; color: #777; transition: all 0.15s; -webkit-tap-highlight-color: transparent; }
  .pf-speed-btn.active { background: rgb(160,0,48); color: #fff; border-color: rgb(160,0,48); }
  .pf-upload-zone { border: 2px dashed rgba(160,0,48,0.25); border-radius: var(--radius); padding: clamp(24px,6vw,40px) var(--sp-md); text-align: center; background: rgba(160,0,48,0.03); cursor: pointer; transition: border-color 0.15s, background 0.15s; margin-bottom: 16px; }
  .pf-upload-zone:hover { border-color: rgb(160,0,48); background: rgba(160,0,48,0.07); }
  .pf-upload-zone input { display: none; }
  .pf-upload-icon { font-size: 2rem; margin-bottom: 10px; }
  .pf-video-preview-bar { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: var(--radius-sm); padding: 12px 16px; margin-bottom: 16px; }
  .pf-video-preview-thumb { width: 54px; height: 38px; border-radius: 6px; background: #1a1a18; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 18px; }
  .pf-video-tab-row { display: flex; border-bottom: 1.5px solid rgba(0,0,0,0.08); margin-bottom: 20px; }
  .pf-video-tab { flex: 1; padding: 11px 0; text-align: center; font-size: var(--fs-sm); font-weight: 600; color: #bbb; cursor: pointer; border-bottom: 2.5px solid transparent; margin-bottom: -1.5px; transition: all 0.15s; -webkit-tap-highlight-color: transparent; }
  .pf-video-tab.active { color: rgb(160,0,48); border-bottom-color: rgb(160,0,48); }
  @media (max-width: 600px) { .pf-chip { min-height: 44px; } .pf-input, .pf-textarea, .pf-select { font-size: 16px; } }
`;

function StyleInjector() {
  useEffect(() => {
    if (!document.querySelector("#pf-css")) {
      const s = document.createElement("style");
      s.id = "pf-css";
      s.textContent = GLOBAL_CSS;
      document.head.appendChild(s);
    }
  }, []);
  return null;
}

// ─── Micro-components ─────────────────────────────────────────────────────────
function Input({ value, onChange, placeholder, type = "text", style = {} }) {
  return <input className="pf-input" type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} style={style} />;
}
function Textarea({ value, onChange, placeholder, rows = 4, style = {} }) {
  return <textarea className="pf-textarea" value={value} rows={rows} placeholder={placeholder} onChange={e => onChange(e.target.value)} style={style} />;
}
function Select({ value, onChange, options, placeholder }) {
  return (
    <div className="pf-select-wrap">
      <select
        className="pf-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ color: value ? "#1a1a18" : "#aaa" }}
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.v || o} value={o.v || o}>{o.l || o}</option>)}
      </select>
      <span className="pf-arrow">▼</span>
    </div>
  );
}
function Chip({ label, selected, radio, onClick }) {
  return (
    <button type="button" className="pf-chip" onClick={onClick} style={{
      border: `1.5px solid ${selected ? P : "rgba(0,0,0,0.1)"}`,
      background: selected ? (radio ? P : PL) : "#fff",
      color: selected ? (radio ? "#fff" : PD) : "#555",
      fontWeight: selected ? 600 : 400,
    }}>{label}</button>
  );
}
function ChipGroup({ options, value, onChange, single = false }) {
  return (
    <div className="pf-chips">
      {options.map(o => {
        const sel = single ? value === o : (value || []).includes(o);
        return (
          <Chip key={o} label={o} selected={sel} radio={single}
            onClick={() => {
              if (single) { onChange(o); return; }
              const arr = value || [];
              onChange(sel ? arr.filter(v => v !== o) : [...arr, o]);
            }} />
        );
      })}
    </div>
  );
}
function Stars({ value, onChange }) {
  const labels = ["", "Just starting out", "Getting comfortable", "Solid professional", "Very experienced", "Top of my field"];
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", gap: "clamp(6px,2vw,14px)", marginBottom: 8 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <span key={n} className="pf-star" onClick={() => onChange(n)} style={{ color: value >= n ? "#c97b2a" : "#e0e0e0", transform: value >= n ? "scale(1.12)" : "scale(1)" }}>★</span>
        ))}
      </div>
      {value > 0 && <p style={{ fontSize: "var(--fs-body)", color: "#888" }}>{labels[value]}</p>}
    </div>
  );
}
function NavRow({ onBack, onNext, nextLabel = "Continue →", disabled = false }) {
  return (
    <div className="pf-nav">
      {onBack && <button type="button" className="pf-back" onClick={onBack}>← Back</button>}
      <button type="button" className="pf-next" onClick={onNext} disabled={disabled}>{nextLabel}</button>
    </div>
  );
}
function SkipLink({ onClick }) { return <p className="pf-skip" onClick={onClick}>Skip for now →</p>; }
function FieldLabel({ children, sub }) {
  return <label className="pf-label">{children}{sub && <span className="pf-label-sub">{sub}</span>}</label>;
}
function InfoBox({ icon = "💡", children }) {
  return <div className="pf-info"><span style={{ flexShrink: 0 }}>{icon}</span><span>{children}</span></div>;
}
function DotBar({ current }) {
  return (
    <div className="pf-dotbar">
      {Array.from({ length: TOTAL_Q }, (_, i) => {
        const n = i + 1, active = n === current, done = n < current;
        return <div key={n} style={{ height: 5, flexShrink: 0, width: active ? 24 : done ? 8 : 6, borderRadius: 3, background: active ? P : done ? PM : "#e8e8e5", transition: "all 0.35s" }} />;
      })}
      <span className="pf-dot-count">{current} / {TOTAL_Q}</span>
    </div>
  );
}
function QHeader({ n, title, hint }) {
  const phases = ["", "About you", "About you", "About you", "Your background", "Your service", "Your service", "Your service", "Availability & pricing", "Communication", "Trust & feedback"];
  return (
    <div style={{ marginBottom: "var(--sp-md)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span className="pf-phase-tag">{phases[n] || "About you"}</span>
        <span className="pf-q-num">{String(n).padStart(2, "0")} / {TOTAL_Q}</span>
      </div>
      <h2 className="pf-q-title">{title}</h2>
      {hint && <p className="pf-q-hint">{hint}</p>}
    </div>
  );
}
function Topbar({ pct }) {
  return (
    <div className="pf-topbar">
      <div className="pf-logo">YU<span style={{ color: P }}>UKKE</span></div>
      <div className="pf-progress-wrap">
        <div className="pf-progress-track"><div className="pf-progress-fill" style={{ width: `${pct}%` }} /></div>
        <span className="pf-pct">{Math.round(pct)}%</span>
      </div>
    </div>
  );
}

// ─── Guard screens ────────────────────────────────────────────────────────────
function InvalidLink() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "var(--ff-body)", background: "#f9f7f4", padding: "var(--sp-md)", textAlign: "center" }}>
      <div style={{ fontSize: 56, marginBottom: 18 }}>🔗</div>
      <h2 style={{ fontFamily: "var(--ff-display)", fontSize: "var(--fs-h2)", color: "#1a1a18", marginBottom: 10 }}>Invalid link</h2>
      <p style={{ color: "#aaa", fontSize: "var(--fs-body)", maxWidth: 320, lineHeight: 1.7 }}>No user ID found. Please contact YUUKKE support or request a new link.</p>
    </div>
  );
}
function SubmitError({ message, onRetry }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "var(--ff-body)", background: "#f9f7f4", padding: "var(--sp-md)", textAlign: "center" }}>
      <div style={{ fontSize: 56, marginBottom: 18 }}>⚠️</div>
      <h2 style={{ fontFamily: "var(--ff-display)", fontSize: "var(--fs-h2)", color: "#1a1a18", marginBottom: 10 }}>Submission failed</h2>
      <p style={{ color: "#aaa", fontSize: "var(--fs-body)", maxWidth: 320, marginBottom: 24, lineHeight: 1.7 }}>{message}</p>
      <button type="button" className="pf-next" style={{ flex: "none", padding: "14px 32px" }} onClick={onRetry}>Try again</button>
    </div>
  );
}

// ─── Completion scorer ────────────────────────────────────────────────────────
function getCompletion(a) {
  const fields = [
    { label: "What you did before",       filled: !!a.q1_before?.trim(),                     screen: 1,    impact: "high"   },
    { label: "Why you became a provider", filled: !!a.q2_why?.trim(),                        screen: 2,    impact: "high"   },
    { label: "Transformation story",      filled: !!a.q3_story?.trim(),                      screen: 3,    impact: "high"   },
    { label: "Qualification",             filled: !!a.q4_qual?.trim(),                       screen: 4,    impact: "high"   },
    { label: "Years of experience",       filled: !!a.q4_exp,                                screen: 4,    impact: "high"   },
    { label: "Service type",              filled: !!a.q5_type,                               screen: 5,    impact: "high"   },
    { label: "Sub-service",               filled: (a.q5_sub || []).length > 0,               screen: 5,    impact: "medium" },
    { label: "Who you help",              filled: (a.q6_who || []).length > 0,               screen: 6,    impact: "high"   },
    { label: "Delivery method",           filled: !!a.q7_delivery,                           screen: 7,    impact: "high"   },
    { label: "Tools & methods",           filled: !!a.q7_tools?.trim(),                      screen: 7,    impact: "medium" },
    { label: "Pricing",                   filled: !!a.q8_price?.trim(),                      screen: 8,    impact: "high"   },
    { label: "Available days",            filled: (a.q8_days || []).length > 0,              screen: 8,    impact: "high"   },
    { label: "Languages spoken",          filled: (a.q9_langs || []).length > 0,             screen: 9,    impact: "high"   },
    { label: "Timezone",                  filled: !!a.q9_tz,                                 screen: 9,    impact: "medium" },
    { label: "Self-rated expertise",      filled: a.q10_stars > 0,                           screen: 10,   impact: "high"   },
    { label: "Client feedback",           filled: !!a.q10_feedback?.trim(),                  screen: 10,   impact: "high"   },
    { label: "Video introduction",        filled: !!a.video_file || !!a.video_recorded_blob, screen: 10.5, impact: "medium" },
  ];
  const filled  = fields.filter(f => f.filled).length;
  const missing = fields.filter(f => !f.filled);
  const pct     = Math.round((filled / fields.length) * 100);
  return { fields, filled, total: fields.length, missing, pct };
}

// ─── Build professional script ────────────────────────────────────────────────
function buildScript(a) {
  const service  = a.q5_type  || "consulting";
  const subs     = (a.q5_sub  || []).slice(0, 3);
  const delivery = a.q7_delivery || "";
  const who      = (a.q6_who  || []).filter(w => w !== "Anyone & everyone").slice(0, 2);
  const tools    = a.q7_tools || "";
  const pricing  = a.q8_price || "";

  const deliveryPhrase = {
    "I visit the client at their home/office": "on-site at your location",
    "Client comes to my place":               "at our premises",
    "Online — video call or phone":           "online via video call or phone, fully flexible",
    "Both online and in-person":              "both online and in-person — whichever suits you",
  }[delivery] || "online and in-person";

  const line1 = `We specialise in ${service}${subs.length ? `, covering ${subs.join(", ")}` : ""}.`;
  const line2 = who.length
    ? `Our services are designed for ${who.join(" and ")}, with every engagement tailored to their specific goals.`
    : "Every engagement is fully tailored to the client's goals and context.";
  const line3 = delivery
    ? `We work ${deliveryPhrase}, making it easy to fit sessions around your schedule.`
    : "We offer flexible delivery — online, in-person, or a combination of both.";
  const line4 = tools
    ? `Our methodology includes ${tools.slice(0, 100)}${tools.length > 100 ? "…" : ""}`
    : "Our approach is structured, outcome-focused, and built around measurable results.";
  const line5 = pricing
    ? `Engagements start at ${pricing}. Book a consultation to get started.`
    : "Reach out today to book your first consultation.";

  return [line1, line2, line3, line4, line5].join(" ");
}

// ─── VIDEO INTRO SCREEN ───────────────────────────────────────────────────────
function VideoIntroScreen({ a, set, onBack, onNext, onSkip }) {
  const [recState,   setRecState]   = useState("idle");
  const [tab,        setTab]        = useState("record");
  const [elapsed,    setElapsed]    = useState(0);
  const [speed,      setSpeed]      = useState("Normal");
  const [camError,   setCamError]   = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [scriptText, setScriptText] = useState(() => a.video_script || buildScript(a));

  const handleScriptBlur = useCallback(() => {
    set("video_script")(scriptText);
  }, [scriptText, set]);

  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef   = useRef([]);
  const timerRef    = useRef(null);
  const MAX_SECS    = 60;

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  function startTimer() {
    timerRef.current = setInterval(() => {
      setElapsed(e => {
        if (e >= MAX_SECS - 1) { stopRecording(); return MAX_SECS; }
        return e + 1;
      });
    }, 1000);
  }
  function stopTimer() { clearInterval(timerRef.current); }
  function fmtTime(s) {
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  }

  async function enableCamera() {
    setCamError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = s;
      const vid = videoRef.current;
      if (vid) {
        vid.srcObject = s;
        vid.muted     = true;
        vid.controls  = false;
        await new Promise((resolve, reject) => {
          vid.onloadedmetadata = () => vid.play().then(resolve).catch(reject);
          if (vid.readyState >= 1) vid.play().then(resolve).catch(reject);
        }).catch(() => {});
      }
      setRecState("ready");
    } catch (err) {
      const isDenied = err && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError");
      setCamError(isDenied
        ? "Camera or microphone access was denied. Please allow permission in your browser settings, then try again."
        : "Could not access camera or microphone. Please check your device and browser settings."
      );
    }
  }

  function handleRecord() {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setElapsed(0);
    setPreviewUrl(null);
    set("video_recorded_blob")(null);

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";

    const mr = new MediaRecorder(streamRef.current, { mimeType });
    mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url  = URL.createObjectURL(blob);
      set("video_recorded_blob")(blob);
      setPreviewUrl(url);
      setRecState("done");
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      const vid = videoRef.current;
      if (vid) { vid.srcObject = null; vid.src = url; vid.muted = false; vid.controls = true; vid.play().catch(() => {}); }
    };
    mr.start(250);
    mediaRecRef.current = mr;
    setRecState("recording");
    startTimer();
  }

  function handlePause() {
    const mr = mediaRecRef.current;
    if (!mr) return;
    if (mr.state === "recording") { mr.pause(); stopTimer(); setRecState("paused"); }
    else if (mr.state === "paused") { mr.resume(); startTimer(); setRecState("recording"); }
  }

  function stopRecording() {
    stopTimer();
    const mr = mediaRecRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  }

  async function handleRetake() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRecState("idle"); setElapsed(0); setPreviewUrl(null);
    set("video_recorded_blob")(null);
    chunksRef.current = []; mediaRecRef.current = null;
    const vid = videoRef.current;
    if (vid) { vid.src = ""; vid.srcObject = null; vid.controls = false; }
    await enableCamera();
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) { alert("File too large. Maximum is 150 MB."); return; }
    set("video_recorded_blob")(null);
    set("video_file")(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  const hasVideo = !!a.video_file || !!a.video_recorded_blob;
  const showPlaceholder = recState === "idle";

  return (
    <>
      <div style={{ marginBottom: "var(--sp-md)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span className="pf-phase-tag">Video intro</span>
          <span className="pf-q-num" style={{ color: "#c97b2a" }}>Optional · High impact</span>
        </div>
        <h2 className="pf-q-title">Add a professional service introduction</h2>
        <p className="pf-q-hint">Providers with a video intro get <strong style={{ color: PD }}>3× more bookings</strong>. Max 60 seconds — keep it professional and service-focused.</p>
      </div>

      <InfoBox icon="🎯">Your script is auto-generated from your service details — outcome-focused, no personal background needed. Edit it freely, then hit record.</InfoBox>

      <div className="pf-video-tab-row">
        <div className={`pf-video-tab${tab === "record" ? " active" : ""}`} onClick={() => setTab("record")}>🎥 Record now</div>
        <div className={`pf-video-tab${tab === "upload" ? " active" : ""}`} onClick={() => setTab("upload")}>📁 Upload video</div>
      </div>

      {tab === "record" && (<>
        {camError && (
          <div style={{ background: "#fff4f4", border: `1px solid ${PM}`, borderRadius: "var(--radius-sm)", padding: "12px 16px", fontSize: "var(--fs-sm)", color: PD, marginBottom: 16 }}>⚠️ {camError}</div>
        )}
        <div className="pf-video-viewport">
          <video ref={videoRef} playsInline autoPlay={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          {showPlaceholder && (
            <div className="pf-video-placeholder">
              <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
              </svg>
              <span>Tap "Enable camera" to preview yourself</span>
            </div>
          )}
          {recState === "recording" && <div className="pf-rec-badge"><div className="pf-rec-dot" />REC</div>}
          {(recState === "recording" || recState === "paused") && <div className="pf-timer-badge">{fmtTime(elapsed)} / {fmtTime(MAX_SECS)}</div>}
          {(recState === "recording" || recState === "paused") && <div className="pf-teleprompter-overlay">{scriptText}</div>}
        </div>

        <div className="pf-video-controls">
          {recState === "idle"      && <button className="pf-ctrl-btn record" onClick={enableCamera}><svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>Enable camera</button>}
          {recState === "ready"     && <button className="pf-ctrl-btn record" onClick={handleRecord}><svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /></svg>Start recording</button>}
          {recState === "recording" && <><button className="pf-ctrl-btn pause" onClick={handlePause}>⏸ Pause</button><button className="pf-ctrl-btn stop" onClick={stopRecording}>⏹ Stop</button></>}
          {recState === "paused"    && <><button className="pf-ctrl-btn record" onClick={handlePause}>▶ Resume</button><button className="pf-ctrl-btn stop" onClick={stopRecording}>⏹ Finish</button></>}
          {recState === "done"      && <button className="pf-ctrl-btn retake" onClick={handleRetake}>↺ Retake</button>}
        </div>

        {(recState === "ready" || recState === "recording" || recState === "paused") && (<>
          <FieldLabel sub="teleprompter scroll speed">Script speed</FieldLabel>
          <div className="pf-speed-row">
            {["Slow", "Normal", "Fast"].map(s => (
              <button key={s} className={`pf-speed-btn${speed === s ? " active" : ""}`} onClick={() => setSpeed(s)}>{s}</button>
            ))}
          </div>
        </>)}

        {recState === "done" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "var(--radius-sm)", padding: "12px 16px", fontSize: "var(--fs-sm)", color: "#166534", marginBottom: 16 }}>
            <span style={{ fontSize: 18 }}>✅</span> Video saved and ready. Hit "Save & continue" below.
          </div>
        )}

        <FieldLabel sub="auto-generated from your service answers · edit freely">Teleprompter script</FieldLabel>
        <textarea className="pf-textarea" rows={6} value={scriptText} onChange={e => setScriptText(e.target.value)} onBlur={handleScriptBlur} placeholder="Your professional service introduction…" style={{ marginBottom: 6, fontSize: "var(--fs-sm)", lineHeight: 1.75 }} />
        <p style={{ fontSize: "var(--fs-sm)", color: "#bbb", marginBottom: 16 }}>Aim for 45–60 seconds when read aloud. Focus on what you deliver and who you help — not personal history.</p>
      </>)}

      {tab === "upload" && (<>
        {!a.video_file ? (
          <label className="pf-upload-zone">
            <input type="file" accept="video/mp4,video/webm,video/quicktime,video/*" onChange={handleFileChange} />
            <div className="pf-upload-icon">🎬</div>
            <div style={{ fontWeight: 600, fontSize: "var(--fs-body)", marginBottom: 6 }}>Tap to choose a video file</div>
            <div style={{ fontSize: "var(--fs-sm)", color: "#aaa" }}>MP4 · MOV · WEBM &nbsp;·&nbsp; Max 150 MB &nbsp;·&nbsp; Max 60 sec</div>
          </label>
        ) : (
          <div className="pf-video-preview-bar">
            <div className="pf-video-preview-thumb">🎬</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.video_file.name}</div>
              <div style={{ fontSize: "var(--fs-sm)", color: "#aaa" }}>{(a.video_file.size / (1024 * 1024)).toFixed(1)} MB</div>
            </div>
            <button type="button" onClick={() => { set("video_file")(null); setPreviewUrl(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 18 }}>✕</button>
          </div>
        )}
        {previewUrl && a.video_file && <video src={previewUrl} controls style={{ width: "100%", borderRadius: "var(--radius)", marginBottom: 16 }} />}
        <FieldLabel sub="suggested script for reference">Suggested script</FieldLabel>
        <div style={{ background: "#f9f7f4", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "var(--radius-sm)", padding: "14px 16px", fontSize: "var(--fs-sm)", color: "#555", lineHeight: 1.75, marginBottom: 16 }}>{scriptText}</div>
      </>)}

      <div className="pf-nav" style={{ marginTop: "var(--sp-md)" }}>
        <button type="button" className="pf-back" onClick={onBack}>← Back</button>
        <button type="button" className="pf-next" onClick={onNext} disabled={recState === "recording"}>{hasVideo ? "Save & continue →" : "Continue →"}</button>
      </div>
      <SkipLink onClick={onSkip} />
    </>
  );
}

// ─── Page shell ───────────────────────────────────────────────────────────────
function W({ pct, children }) {
  return (
    <><StyleInjector />
      <div className="pf-outer"><Topbar pct={pct} /><div className="pf-content">{children}</div></div>
    </>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ProviderForm() {
  const { userId } = useParams();
  useLocation();
  const isValid = userId && /^\d+$/.test(userId);

  const [screen,          setScreen]          = useState(0);
  const [submitting,      setSubmitting]      = useState(false);
  const [submitStatus,    setSubmitStatus]    = useState(null);
  const [errorMsg,        setErrorMsg]        = useState("");

  // ── Category data from API ─────────────────────────────────────────────────
  // categoryMap: { "Consulting": ["Academic Consulting", "Business Consulting", ...], ... }
  const [categoryMap,     setCategoryMap]     = useState({});
  const [categoryLoading, setCategoryLoading] = useState(true);

  useEffect(() => {
    MyYuukke.getCategoryListV2()
      .then(res => {
        const data = res?.data?.data || [];
        const map  = {};
        data.forEach(({ categoryName, subCategoryName }) => {
          if (!map[categoryName]) map[categoryName] = [];
          if (!map[categoryName].includes(subCategoryName)) {
            map[categoryName].push(subCategoryName);
          }
        });
        setCategoryMap(map);
      })
      .catch(() => setCategoryMap({}))
      .finally(() => setCategoryLoading(false));
  }, []);

  const [a, setA] = useState({
    q1_before_type: "", q1_before: "",
    q2_why: "",         q3_story: "",
    q4_qual: "",        q4_exp: "",   q4_country: "",
    q5_type: "",        q5_sub: [],   q5_other: "",
    q6_who: [],         q6_count: "",
    q7_delivery: "",    q7_tools: "",
    q8_price: "",       q8_deal: "",  q8_days: [], q8_slots: [],
    q9_langs: [],       q9_wa: "",    q9_tz: "",
    q10_stars: 0,       q10_feedback: "", q10_update: "",
    video_file:          null,
    video_recorded_blob: null,
    video_script:        "",
    video_skipped:       false,
  });

  const set = useCallback((k) => (v) => setA(prev => ({ ...prev, [k]: v })), []);

  const goTo = (n) => { setScreen(n); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const pct  = Math.min(Math.max((screen / (TOTAL_Q + 1)) * 100, 0), 100);

  function buildPayload() {
    return {
      userId,
      submittedAt:         new Date().toISOString(),
      beforeType:          a.q1_before_type,
      whatDidBefore:       a.q1_before,
      whyBecameProvider:   a.q2_why,
      transformationStory: a.q3_story,
      qualification:       a.q4_qual,
      yearsExperience:     a.q4_exp,
      country:             a.q4_country,
      serviceType:         a.q5_type,
      subServices:         a.q5_sub,
      serviceOther:        a.q5_other,
      clientTypes:         a.q6_who,
      clientsServed:       a.q6_count,
      deliveryMethod:      a.q7_delivery,
      toolsAndApproach:    a.q7_tools,
      pricing:             a.q8_price,
      specialDeals:        a.q8_deal,
      availableDays:       a.q8_days,
      timeSlots:           a.q8_slots,
      languages:           a.q9_langs,
      whatsapp:            a.q9_wa,
      timezone:            a.q9_tz,
      selfRating:          a.q10_stars,
      clientFeedback:      a.q10_feedback,
      skillsUpdate:        a.q10_update,
      videoSkipped:        a.video_skipped,
    };
  }

  function submitForm() {
    if (!isValid) return;
    setSubmitting(true);
    setErrorMsg("");

    const payload  = buildPayload();
    const hasVideo = a.video_file || a.video_recorded_blob;

    if (hasVideo) {
      const fd = new FormData();
      // JSON part — type must be application/json so Spring @RequestPart deserializes it
      fd.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
      // Video part — wrap recorded Blob in a File so browser sends a filename
      const videoBlob = a.video_file
        ? a.video_file
        : new File([a.video_recorded_blob], "intro.webm", { type: "video/webm" });
      fd.append("video", videoBlob);

      MyYuukke.submitProfileWithVideo(fd)
        .then(() => goTo(12))
        .catch(err => {
          setSubmitStatus("error");
          setErrorMsg(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
        })
        .finally(() => setSubmitting(false));
    } else {
      MyYuukke.submitProfile(payload)
        .then(() => goTo(12))
        .catch(err => {
          setSubmitStatus("error");
          setErrorMsg(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
        })
        .finally(() => setSubmitting(false));
    }
  }

  if (!isValid) return <><StyleInjector /><InvalidLink /></>;
  if (submitStatus === "error") return <><StyleInjector /><SubmitError message={errorMsg} onRetry={() => { setSubmitStatus(null); goTo(11); }} /></>;

  // ── Screen 0: Welcome ──────────────────────────────────────────────────────
  if (screen === 0) return (
    <W pct={pct}>
      <div style={{ textAlign: "center", paddingTop: "var(--sp-sm)" }}>
        <div className="pf-welcome-icon">✨</div>
        <h1 style={{ fontFamily: "var(--ff-display)", fontSize: "var(--fs-hero)", fontWeight: 400, color: "#1a1a18", marginBottom: 14, letterSpacing: "-1.5px", lineHeight: 1.1 }}>Tell us your story</h1>
        <p style={{ fontSize: "var(--fs-body)", color: "#777", maxWidth: 420, margin: "0 auto 8px", lineHeight: 1.75 }}>
          We already have your name, email, and phone. Now we need to understand <em>who you are</em> and <em>what you offer</em>.
        </p>
        <p style={{ fontSize: "var(--fs-sm)", color: "#bbb", marginBottom: "var(--sp-lg)" }}>⏱ About 4–5 minutes · 10 questions + optional video</p>
        <div className="pf-grid-2" style={{ maxWidth: 520, margin: "0 auto var(--sp-lg)" }}>
          {[
            ["🧠", "Your story",      "What you did before & why you made the shift"],
            ["🎯", "Your background", "Qualifications & years of experience"],
            ["🛠", "Your service",    "Type, sub-type, delivery & pricing"],
            ["⭐", "Your reviews",    "Client feedback, languages & availability"],
          ].map(([ic, t, s]) => (
            <div key={t} style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.07)", borderRadius: "var(--radius)", padding: "clamp(14px,3.5vw,20px)", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", textAlign: "left" }}>
              <div style={{ fontSize: "clamp(1.4rem,5vw,1.8rem)", marginBottom: 10 }}>{ic}</div>
              <strong style={{ display: "block", fontWeight: 600, fontSize: "var(--fs-body)", marginBottom: 4 }}>{t}</strong>
              <span style={{ color: "#aaa", fontSize: "var(--fs-sm)", lineHeight: 1.55 }}>{s}</span>
            </div>
          ))}
        </div>
        <button type="button" className="pf-next" onClick={() => goTo(1)} style={{ maxWidth: 320, margin: "0 auto", display: "block", flex: "none" }}>Let's go →</button>
      </div>
    </W>
  );

  // ── Screen 1 ───────────────────────────────────────────────────────────────
  if (screen === 1) return (
    <W pct={pct}><DotBar current={1} />
      <QHeader n={1} title="What were you doing before this?" hint="Tell us about your previous work, career, or life before you started offering this service." />
      <ChipGroup single value={a.q1_before_type} onChange={set("q1_before_type")} options={["I had a corporate job", "I ran my own business", "I was a student", "I was a homemaker", "I was in a different profession", "This is my first real work"]} />
      <FieldLabel>Tell us more</FieldLabel>
      <Textarea value={a.q1_before} onChange={set("q1_before")} rows={3} style={{ marginBottom: 4 }} placeholder="e.g. I worked as a software engineer for 8 years before I started teaching coding to kids..." />
      <NavRow onBack={() => goTo(0)} onNext={() => goTo(2)} />
    </W>
  );

  // ── Screen 2 ───────────────────────────────────────────────────────────────
  if (screen === 2) return (
    <W pct={pct}><DotBar current={2} />
      <QHeader n={2} title="Why did you decide to offer this service?" hint="What pushed you to start? A problem you faced, a passion you discovered, a gap you noticed?" />
      <InfoBox icon="💬">Profiles with a personal "why" get <strong>4× more client trust</strong>. Just be honest.</InfoBox>
      <Textarea value={a.q2_why} onChange={set("q2_why")} rows={6} placeholder="e.g. My younger sister struggled in school and no one understood her learning style..." />
      <NavRow onBack={() => goTo(1)} onNext={() => goTo(3)} />
      <SkipLink onClick={() => goTo(3)} />
    </W>
  );

  // ── Screen 3 ───────────────────────────────────────────────────────────────
  if (screen === 3) return (
    <W pct={pct}><DotBar current={3} />
      <QHeader n={3} title="Share one moment that made you sure this was right" hint="A client win, a breakthrough, a conversation that changed things." />
      <InfoBox icon="⭐">This becomes the centerpiece of your profile. Specific and real always beats vague and impressive.</InfoBox>
      <Textarea value={a.q3_story} onChange={set("q3_story")} rows={6} placeholder="e.g. A 45-year-old woman came to me saying she'd never cooked a proper meal. Six sessions later she cooked her daughter's wedding reception food by herself..." />
      <NavRow onBack={() => goTo(2)} onNext={() => goTo(4)} />
      <SkipLink onClick={() => goTo(4)} />
    </W>
  );

  // ── Screen 4 ───────────────────────────────────────────────────────────────
  if (screen === 4) return (
    <W pct={pct}><DotBar current={4} />
      <QHeader n={4} title="What's your background and experience?" hint="Formal qualifications, self-taught skills, or years of practice — all count equally." />
      <FieldLabel>Your qualification or biggest achievement</FieldLabel>
      <Input value={a.q4_qual} onChange={set("q4_qual")} style={{ marginBottom: 20 }} placeholder="e.g. MBA, 10 years of practice, Certified Yoga Instructor" />
      <FieldLabel>Years of experience</FieldLabel>
      <ChipGroup single value={a.q4_exp} onChange={set("q4_exp")} options={["Less than 1 year", "1–3 years", "3–5 years", "5–10 years", "More than 10 years"]} />
      <FieldLabel>Country you're based in</FieldLabel>
      <Input value={a.q4_country} onChange={set("q4_country")} style={{ marginBottom: 4 }} placeholder="e.g. India, UAE, UK, Singapore" />
      <NavRow onBack={() => goTo(3)} onNext={() => goTo(5)} />
    </W>
  );

  // ── Screen 5 — API-driven category + sub-category ─────────────────────────
  const subOptions = categoryMap[a.q5_type] || [];
  if (screen === 5) return (
    <W pct={pct}><DotBar current={5} />
      <QHeader n={5} title="What service do you offer?" hint="Choose your main category, then pick specific areas within it." />
      <FieldLabel>Main service category</FieldLabel>
      <div style={{ marginBottom: 20 }}>
        {categoryLoading ? (
          <div style={{ padding: "14px 20px", background: "#fff", border: "1.5px solid rgba(0,0,0,0.12)", borderRadius: "var(--radius)", color: "#aaa", fontSize: "var(--fs-body)" }}>
            Loading categories…
          </div>
        ) : (
          <Select
            value={a.q5_type}
            onChange={v => setA(prev => ({ ...prev, q5_type: v, q5_sub: [] }))}
            placeholder="Select your main service type"
            options={Object.keys(categoryMap)}
          />
        )}
      </div>
      {a.q5_type && subOptions.length > 0 && <>
        <FieldLabel>Specific areas <span className="pf-label-sub">(pick all that apply)</span></FieldLabel>
        <ChipGroup options={subOptions} value={a.q5_sub} onChange={set("q5_sub")} />
      </>}
      <NavRow onBack={() => goTo(4)} onNext={() => goTo(6)} disabled={!a.q5_type || categoryLoading} />
    </W>
  );

  // ── Screen 6 ───────────────────────────────────────────────────────────────
  if (screen === 6) return (
    <W pct={pct}><DotBar current={6} />
      <QHeader n={6} title="Who do you typically work with?" hint="Clients search for providers who specialize in helping people like them." />
      <FieldLabel>Your typical clients <span className="pf-label-sub">(pick all that apply)</span></FieldLabel>
      <ChipGroup options={["School children (age 5–12)", "Teenagers (13–18)", "College students", "Working adults", "Homemakers", "Senior citizens (60+)", "Entrepreneurs", "Corporate teams", "Anyone & everyone"]} value={a.q6_who} onChange={set("q6_who")} />
      <FieldLabel>Roughly how many clients have you served?</FieldLabel>
      <ChipGroup single value={a.q6_count} onChange={set("q6_count")} options={["Just starting (1–10)", "Growing (10–50)", "Established (50–200)", "Experienced (200+)"]} />
      <NavRow onBack={() => goTo(5)} onNext={() => goTo(7)} />
    </W>
  );

  // ── Screen 7 ───────────────────────────────────────────────────────────────
  if (screen === 7) return (
    <W pct={pct}><DotBar current={7} />
      <QHeader n={7} title="How do you deliver your service?" hint="Tell clients where and how they can work with you." />
      <FieldLabel>Delivery method</FieldLabel>
      <ChipGroup single value={a.q7_delivery} onChange={set("q7_delivery")} options={["I visit the client at their home/office", "Client comes to my place", "Online — video call or phone", "Both online and in-person"]} />
      <FieldLabel sub="optional">Your tools, approach, or methods</FieldLabel>
      <Textarea value={a.q7_tools} onChange={set("q7_tools")} rows={4} style={{ marginBottom: 4 }} placeholder="e.g. I use structured lesson plans, weekly practice tests, and a WhatsApp group for daily questions." />
      <NavRow onBack={() => goTo(6)} onNext={() => goTo(8)} disabled={!a.q7_delivery} />
    </W>
  );

  // ── Screen 8 ───────────────────────────────────────────────────────────────
  if (screen === 8) return (
    <W pct={pct}><DotBar current={8} />
      <QHeader n={8} title="When are you available and what do you charge?" hint="Be as specific as you can — this is what clients see before booking." />
      <FieldLabel>Your pricing</FieldLabel>
      <Input value={a.q8_price} onChange={set("q8_price")} style={{ marginBottom: 16 }} placeholder="e.g. ₹500/hour, ₹2000 per session, Free first call then ₹800" />
      <FieldLabel sub="optional">Special deals or packages</FieldLabel>
      <Input value={a.q8_deal} onChange={set("q8_deal")} style={{ marginBottom: 20 }} placeholder="e.g. 5 sessions for ₹3500, First session free" />
      <FieldLabel>Which days are you available?</FieldLabel>
      <ChipGroup options={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]} value={a.q8_days} onChange={set("q8_days")} />
      <FieldLabel>What time slots work for you?</FieldLabel>
      <ChipGroup options={["Early morning (6–9am)", "Morning (9am–12pm)", "Afternoon (12–5pm)", "Evening (5–9pm)", "Flexible / by request"]} value={a.q8_slots} onChange={set("q8_slots")} />
      <NavRow onBack={() => goTo(7)} onNext={() => goTo(9)} disabled={!a.q8_price} />
    </W>
  );

  // ── Screen 9 ───────────────────────────────────────────────────────────────
  if (screen === 9) return (
    <W pct={pct}><DotBar current={9} />
      <QHeader n={9} title="How do clients reach you?" hint="Language is one of the top filters clients use. Add every language you can work in." />
      <FieldLabel>Languages you can work in</FieldLabel>
      <ChipGroup options={["English", "Tamil", "Hindi", "Telugu", "Malayalam", "Kannada", "Bengali", "Marathi", "Urdu", "Punjabi", "French", "Arabic", "Mandarin"]} value={a.q9_langs} onChange={set("q9_langs")} />
      <div className="pf-grid-2" style={{ marginBottom: 4 }}>
        <div>
          <FieldLabel sub="optional">WhatsApp number</FieldLabel>
          <Input value={a.q9_wa} onChange={set("q9_wa")} type="tel" placeholder="+91 98765 43210" />
        </div>
        <div>
          <FieldLabel>Your timezone</FieldLabel>
          <Select value={a.q9_tz} onChange={set("q9_tz")} placeholder="Select timezone"
            options={[
              { v: "Asia/Kolkata",      l: "India (IST, UTC+5:30)"    },
              { v: "Asia/Dubai",        l: "UAE / Gulf (GST, UTC+4)"  },
              { v: "Asia/Singapore",    l: "Singapore (SGT, UTC+8)"   },
              { v: "Asia/Colombo",      l: "Sri Lanka (UTC+5:30)"     },
              { v: "Europe/London",     l: "UK (GMT/BST)"             },
              { v: "America/New_York",  l: "US East (EST/EDT)"        },
              { v: "America/Los_Angeles", l: "US West (PST/PDT)"      },
              { v: "Australia/Sydney",  l: "Australia East (AEST)"    },
            ]} />
        </div>
      </div>
      <NavRow onBack={() => goTo(8)} onNext={() => goTo(10)} />
    </W>
  );

  // ── Screen 10 ──────────────────────────────────────────────────────────────
  if (screen === 10) return (
    <W pct={pct}><DotBar current={10} />
      <QHeader n={10} title="How would your past clients describe you?" hint="Rate yourself honestly — clients trust realistic scores more than a perfect 5 stars." />
      <FieldLabel>Your self-rated expertise</FieldLabel>
      <Stars value={a.q10_stars} onChange={set("q10_stars")} />
      <FieldLabel>What do clients typically say about working with you?</FieldLabel>
      <Textarea value={a.q10_feedback} onChange={set("q10_feedback")} rows={5} style={{ marginBottom: 16 }} placeholder="e.g. My clients say I'm patient, I explain things in simple terms, and I always follow up. Many refer their friends and family to me." />
      <FieldLabel sub="optional">How do you keep your skills current?</FieldLabel>
      <Input value={a.q10_update} onChange={set("q10_update")} style={{ marginBottom: 4 }} placeholder="e.g. Monthly workshops, reading industry journals, practising daily" />
      <NavRow onBack={() => goTo(9)} onNext={() => goTo(10.5)} nextLabel="Continue →" />
    </W>
  );

  // ── Screen 10.5: Video ─────────────────────────────────────────────────────
  if (screen === 10.5) return (
    <W pct={pct}>
      <VideoIntroScreen a={a} set={set} onBack={() => goTo(10)} onNext={() => goTo(11)} onSkip={() => { set("video_skipped")(true); goTo(11); }} />
    </W>
  );

  // ── Screen 11: Review ──────────────────────────────────────────────────────
  if (screen === 11) {
    const hasVideo = !!a.video_file || !!a.video_recorded_blob;
    const rows = [
      { l: "What you did before",       v: a.q1_before,                  e: 1    },
      { l: "Why you became a provider", v: a.q2_why,                     e: 2    },
      { l: "Your key moment",           v: a.q3_story,                   e: 3    },
      { l: "Qualification",             v: a.q4_qual,                    e: 4    },
      { l: "Years of experience",       v: a.q4_exp,                     e: 4    },
      { l: "Country",                   v: a.q4_country,                 e: 4    },
      { l: "Service type",              v: a.q5_type,                    e: 5    },
      { l: "Sub-services",              v: (a.q5_sub || []).join(", "),  e: 5    },
      { l: "Who you help",              v: (a.q6_who || []).join(", "),  e: 6    },
      { l: "Clients served",            v: a.q6_count,                   e: 6    },
      { l: "Delivery method",           v: a.q7_delivery,                e: 7    },
      { l: "Tools & methods",           v: a.q7_tools,                   e: 7    },
      { l: "Pricing",                   v: a.q8_price,                   e: 8    },
      { l: "Special deals",             v: a.q8_deal,                    e: 8    },
      { l: "Available days",            v: (a.q8_days  || []).join(", "),e: 8    },
      { l: "Time slots",                v: (a.q8_slots || []).join(", "),e: 8    },
      { l: "Languages",                 v: (a.q9_langs || []).join(", "),e: 9    },
      { l: "Timezone",                  v: a.q9_tz,                      e: 9    },
      { l: "Self-rating",               v: a.q10_stars ? `${a.q10_stars} / 5 stars` : "", e: 10 },
      { l: "Client feedback",           v: a.q10_feedback,               e: 10   },
      { l: "Video intro",               v: hasVideo ? (a.video_file ? `📁 ${a.video_file.name}` : "🎥 Recorded") : a.video_skipped ? "Skipped" : "Not added", e: 10.5 },
    ];
    return (
      <W pct={pct}>
        <div style={{ marginBottom: "var(--sp-md)" }}>
          <span className="pf-phase-tag">Review</span>
          <h2 style={{ fontFamily: "var(--ff-display)", fontSize: "var(--fs-h2)", fontWeight: 400, color: "#1a1a18", margin: "14px 0 6px" }}>Check everything looks right</h2>
          <p style={{ fontSize: "var(--fs-sm)", color: "#999" }}>Tap "Edit" on any answer to go back and change it.</p>
        </div>
        {rows.map(r => (
          <div key={r.l} className="pf-review-card">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "var(--fs-label)", color: "#bbb", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.07em" }}>{r.l}</div>
              <div style={{ fontSize: "var(--fs-body)", color: r.v ? "#1a1a18" : "#ccc", lineHeight: 1.5, wordBreak: "break-word" }}>{r.v || "Not filled"}</div>
            </div>
            <button type="button" onClick={() => goTo(r.e)} style={{ fontSize: "var(--fs-sm)", color: P, cursor: "pointer", padding: "7px 16px", border: `1px solid ${PM}`, borderRadius: "var(--radius-sm)", background: PL, fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>Edit</button>
          </div>
        ))}
        <button type="button" className="pf-next" style={{ width: "100%", marginTop: 20 }} onClick={() => goTo(11.5)}>Submit my profile →</button>
      </W>
    );
  }

  // ── Screen 11.5: Completion ring + submit ──────────────────────────────────
  if (screen === 11.5) {
    const { missing, pct: cp, filled, total } = getCompletion(a);
    const highMissing = missing.filter(f => f.impact === "high");
    const medMissing  = missing.filter(f => f.impact === "medium");
    const ringColor   = cp >= 80 ? "#1a9e5e" : cp >= 50 ? "#c97b2a" : P;
    const C = 2 * Math.PI * 48;
    return (
      <W pct={pct}>
        <div style={{ textAlign: "center", marginBottom: "var(--sp-md)" }}>
          <div style={{ fontSize: "clamp(2.8rem,10vw,4rem)", marginBottom: 12 }}>🙏</div>
          <h2 style={{ fontFamily: "var(--ff-display)", fontSize: "var(--fs-h2)", fontWeight: 400, color: "#1a1a18", marginBottom: 10 }}>Thank you for sharing your story!</h2>
          <p style={{ fontSize: "var(--fs-body)", color: "#777", maxWidth: 440, margin: "0 auto", lineHeight: 1.7 }}>Every answer helps us match you with the right clients. Here's how your profile looks.</p>
        </div>
        <div className="pf-ring-card">
          <div style={{ position: "relative", width: "clamp(90px,20vw,120px)", height: "clamp(90px,20vw,120px)", margin: "0 auto 16px" }}>
            <svg width="100%" height="100%" viewBox="0 0 110 110">
              <circle cx="55" cy="55" r="48" fill="none" stroke="#f0ede9" strokeWidth="10" />
              <circle cx="55" cy="55" r="48" fill="none" stroke={ringColor} strokeWidth="10" strokeDasharray={C} strokeDashoffset={C * (1 - cp / 100)} strokeLinecap="round" transform="rotate(-90 55 55)" />
            </svg>
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
              <div style={{ fontSize: "clamp(1.2rem,4vw,1.6rem)", fontWeight: 700, color: ringColor, lineHeight: 1 }}>{cp}%</div>
              <div style={{ fontSize: "var(--fs-sm)", color: "#bbb", marginTop: 3 }}>{filled}/{total}</div>
            </div>
          </div>
          <div style={{ fontSize: "var(--fs-body)", fontWeight: 600, color: "#1a1a18", marginBottom: 6 }}>
            {cp === 100 ? "🎉 100% complete!" : cp >= 80 ? "Almost there!" : "Good start — a few more will help a lot"}
          </div>
          <p style={{ fontSize: "var(--fs-sm)", color: "#888", margin: 0 }}>
            {cp === 100 ? "You're eligible for all corporate recommendations on YUUKKE." : `${missing.length} field${missing.length !== 1 ? "s" : ""} missing — completing them gets you 5× better reach.`}
          </p>
        </div>

        {highMissing.length > 0 && (
          <div style={{ background: "#fff8f9", border: `1.5px solid ${PM}`, borderRadius: "var(--radius)", padding: "var(--sp-sm) var(--sp-md)", marginBottom: 16 }}>
            <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: P, marginBottom: 12 }}>⚡ High-impact fields missing</div>
            {highMissing.map(f => (
              <div key={f.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 10, marginBottom: 10, borderBottom: `1px solid rgba(160,0,48,0.07)` }}>
                <span style={{ fontSize: "var(--fs-body)", color: "#555" }}>○ {f.label}</span>
                <button type="button" onClick={() => goTo(f.screen)} style={{ fontSize: "var(--fs-sm)", color: P, cursor: "pointer", padding: "6px 16px", border: `1px solid ${PM}`, borderRadius: "var(--radius-sm)", background: PL, fontFamily: "inherit" }}>Fill now</button>
              </div>
            ))}
          </div>
        )}

        {medMissing.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.07)", borderRadius: "var(--radius)", padding: "var(--sp-sm) var(--sp-md)", marginBottom: 20 }}>
            <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: "#aaa", marginBottom: 10 }}>Optional but helpful</div>
            <div className="pf-chips">
              {medMissing.map(f => (
                <button key={f.label} type="button" className="pf-chip" onClick={() => goTo(f.screen)} style={{ border: "1px solid rgba(0,0,0,0.1)", background: "#f7f5f3", color: "#666" }}>+ {f.label}</button>
              ))}
            </div>
          </div>
        )}

        <button type="button" className="pf-next" style={{ width: "100%", marginBottom: 12 }} onClick={submitForm} disabled={submitting}>
          {submitting ? "Submitting…" : cp === 100 ? "Submit — 100% complete! 🎉" : "Submit as is →"}
        </button>
        {cp < 100 && <p style={{ textAlign: "center", fontSize: "var(--fs-sm)", color: "#bbb", lineHeight: 1.6 }}>You can submit now and update anytime. Filling missing fields unlocks 5× more reach.</p>}
      </W>
    );
  }

  // ── Screen 12: Success ─────────────────────────────────────────────────────
  if (screen === 12) {
    const { pct: cp, missing: ms } = getCompletion(a);
    const hasVideo = !!a.video_file || !!a.video_recorded_blob;
    return (
      <W pct={pct}>
        <div style={{ textAlign: "center", paddingTop: "var(--sp-sm)" }}>
          <div style={{ width: "clamp(64px,15vw,84px)", height: "clamp(64px,15vw,84px)", borderRadius: "50%", background: P, margin: "0 auto var(--sp-md)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 28px rgba(160,0,48,0.3)" }}>
            <svg width="45%" height="45%" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <h1 style={{ fontFamily: "var(--ff-display)", fontSize: "var(--fs-hero)", fontWeight: 400, color: "#1a1a18", marginBottom: 12, letterSpacing: "-1.5px", lineHeight: 1.1 }}>Profile submitted!</h1>
          <p style={{ fontSize: "var(--fs-body)", color: "#777", maxWidth: 420, margin: "0 auto var(--sp-md)", lineHeight: 1.75 }}>Your story has been saved. Your YUUKKE profile is being set up and will be live within minutes.</p>

          <div style={{ background: cp === 100 ? "#e8f8f0" : PL, border: `1px solid ${cp === 100 ? "#1a9e5e40" : PM}`, borderRadius: "var(--radius)", padding: "var(--sp-sm) var(--sp-md)", maxWidth: 440, margin: "0 auto var(--sp-md)", textAlign: "left" }}>
            <div style={{ fontSize: "var(--fs-body)", fontWeight: 600, color: cp === 100 ? "#1a9e5e" : PD, marginBottom: 6 }}>
              {cp === 100 ? "✅ 100% complete — full corporate reach unlocked!" : `⚡ Profile is ${cp}% complete`}
            </div>
            {ms.length > 0 && <div style={{ fontSize: "var(--fs-sm)", color: "#888", lineHeight: 1.6 }}>Log in to your dashboard to add: {ms.slice(0, 3).map(f => f.label).join(", ")}{ms.length > 3 ? ` +${ms.length - 3} more` : ""}</div>}
          </div>

          <div className="pf-grid-2" style={{ maxWidth: 480, margin: "0 auto var(--sp-md)" }}>
            {[
              ["✓ Profile headline",       "Generated from your story"      ],
              ["✓ Service description",    "Written in your voice"          ],
              ["✓ Expertise & background", "From your journey & story"      ],
              ["✓ Availability & pricing", "Ready to accept bookings"       ],
              ...(hasVideo ? [["✓ Video intro", "Uploaded to your profile"]] : []),
            ].map(([t, s]) => (
              <div key={t} style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.07)", borderRadius: "var(--radius)", padding: "clamp(12px,3vw,18px)", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <strong style={{ display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", color: P, marginBottom: 4 }}>{t}</strong>
                <span style={{ color: "#aaa", fontSize: "var(--fs-sm)" }}>{s}</span>
              </div>
            ))}
          </div>

          {!hasVideo && (
            <div style={{ background: "#fffbf0", border: "1px solid #f0d060", borderRadius: "var(--radius)", padding: "var(--sp-sm) var(--sp-md)", maxWidth: 460, margin: "0 auto var(--sp-md)", textAlign: "left" }}>
              <strong style={{ color: "#7a5200" }}>💡 Add a video intro later</strong>
              <p style={{ fontSize: "var(--fs-sm)", color: "#7a5200", marginTop: 4 }}>Providers with a video get 3× more bookings. You can record or upload from your dashboard anytime.</p>
            </div>
          )}

          <div style={{ background: PL, border: `1px solid ${PM}`, borderRadius: "var(--radius)", padding: "var(--sp-sm) var(--sp-md)", fontSize: "var(--fs-sm)", color: PD, textAlign: "left", maxWidth: 460, margin: "0 auto" }}>
            <strong>What happens next?</strong> Your profile will be reviewed and activated within 24 hours. You'll receive a notification once it's live.
          </div>
        </div>
      </W>
    );
  }

  return null;
}